import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../axios";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../../constants";
import "../css/FormStyles.css";
import LoadingIndicator from "./LoadingIndicator";

function Form({ route, method, onSuccess }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((prev) => !prev);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const { username, email, password, confirmPassword } = formData;

    if (method === "register") {
      if (!username || !email || !password || !confirmPassword) {
        return "Please fill in all fields.";
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return "Invalid email address.";
      }

      const strongPasswordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

      if (!strongPasswordRegex.test(password)) {
        return "Password must be at least 8 characters, include uppercase, lowercase, number, and symbol.";
      }

      if (password !== confirmPassword) {
        return "Passwords do not match.";
      }
    } else {
      if (!formData.email || !formData.password) {
        return "Email and password are required.";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setLoading(true);

    const payload =
      method === "login"
        ? {
            email: formData.email,
            password: formData.password,
          }
        : {
            username: formData.username,
            email: formData.email,
            password: formData.password,
            confirm_password: formData.confirmPassword,
          };

    try {
      const res = await api.post(route, payload);

      if (method === "login") {
        localStorage.setItem(ACCESS_TOKEN, res.data.access);
        localStorage.setItem(REFRESH_TOKEN, res.data.refresh);
        onSuccess ? onSuccess() : navigate("/dataset-submissions");
      } else {
        setSuccessMsg(
          "Registration successful! Check your email to verify your account."
        );
        setTimeout(() => navigate("/login"), 1500);
      }
    } catch (error) {
      let message = "Something went wrong. Please try again.";

      if (error.response?.data) {
        if (typeof error.response.data === "object") {
          message = Object.entries(error.response.data)
            .map(
              ([key, val]) =>
                `${key}: ${Array.isArray(val) ? val.join(", ") : val}`
            )
            .join(" | ");
        } else if (typeof error.response.data === "string") {
          message = error.response.data;
        }
      }

      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <form className="login-form" onSubmit={handleSubmit}>
        <h2>{method === "login" ? "Login" : "Signup"}</h2>

        {method === "register" && (
          <div className="form-group">
            <input
              type="text"
              name="username"
              placeholder="Username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
            />
          </div>
        )}

        <div className="form-group">
          <input
            type="text"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
        </div>

        <div className="form-group password-toggle">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={method === "register" ? "Create Password" : "Password"}
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete={method === "register" ? "new-password" : "current-password"}
          />
          <span className="toggle-icon" onClick={togglePasswordVisibility}>
            {showPassword ? "🙈" : "👁️"}
          </span>
        </div>

        {method === "register" && (
          <div className="form-group password-toggle">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
            />
            <span
              className="toggle-icon"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? "🙈" : "👁️"}
            </span>
          </div>
        )}

        {errorMsg && <p className="error-message">{errorMsg}</p>}
        {successMsg && <p className="success-message">{successMsg}</p>}
        {loading && <LoadingIndicator />}

        <br />

        <button type="submit" className="signin" disabled={loading}>
          <span>{method === "login" ? "Login" : "Sign Up"}</span>
        </button>

        {method === "login" && (
          <>
            <button
              type="button"
              className="forgot-password"
              onClick={() => navigate("/forgot-password")}
            >
              Forgot password?
            </button>
            <p>
              Don’t have an account?{" "}
              <button
                type="button"
                className="link-btn"
                onClick={() => navigate("/register")}
              >
                Signup
              </button>
            </p>
          </>
        )}

        {method === "register" && (
          <p>
            Already have an account?{" "}
            <button
              type="button"
              className="link-btn"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </p>
        )}
      </form>
    </div>
  );
}

export default Form;

